import React from "react";
import type { IconName } from "@/types/icons";

export interface IconProps extends React.SVGProps<SVGSVGElement> {
  /**
   * The name of the icon (should match the SVG file name without extension)
   */
  name: IconName;
  /**
   * Size of the icon (width and height)
   */
  size?: number | string;
  /**
   * Additional CSS classes
   */
  className?: string;
  /**
   * Accessible title for the icon
   */
  title?: string;
  /**
   * Whether the icon is decorative (hides from screen readers)
   * @default false
   */
  decorative?: boolean;
}

/**
 * Icon component that uses SVG sprite
 *
 * Usage:
 * ```tsx
 * <Icon name="robot" size={24} className="text-purple" />
 * <Icon name="hotballoon" size={48} title="Hot Air Balloon" />
 * ```
 */
export const Icon: React.FC<IconProps> = ({
  name,
  size,
  className = "",
  title,
  decorative = false,
  ...props
}) => {
  const sizeValue = typeof size === "number" ? `${size}px` : size;

  return (
    <svg
      width={sizeValue}
      height={sizeValue}
      className={className}
      fill="none"
      aria-hidden={decorative ? "true" : undefined}
      role={decorative ? undefined : "img"}
      aria-label={!decorative && !title ? name : undefined}
      {...props}
    >
      {title && <title>{title}</title>}
      <use href={`/sprite.svg#${name}`} width="100%" height="100%" />
    </svg>
  );
};
